/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useRef } from "react";
import interact from "interactjs";
import PlayerSVG from "./PlayerSVGV2.js";
import {
  tap,
  moveObject,
  editObject,
  interactionStart,
} from "../reducers/slices/phases.js";
import PropTypes from "prop-types";
import useCurrentValue from "../../hooks/useCurrentValue.js";
import DragCircle from "./DragCircle.js";
import { useTheme } from "@mui/material/styles";
import Helper from "../functions/Helper.js";

export default function Player(props) {
  const dispatch = props.dispatch;
  const propsRef = useCurrentValue(props);

  const [dragDelta, setDragDelta] = useState({ x: 0, y: 0 });
  const dragDeltaRef = useRef({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    if (props.editable && props.subType !== "hoop") {
      interact("#" + props.uId)
        .on("contextmenu", (event) => {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          if (propsRef.current.status !== "EDIT") {
            dispatch(
              tap({
                id: props.id,
                ctrlKey:
                  event.originalEvent.ctrlKey || event.originalEvent.metaKey,
              })
            );
          }
        })
        .on("tap", (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (propsRef.current.status !== "EDIT") {
            //check if macos command key or windows ctrl key is pressed

            dispatch(
              tap({
                id: props.id,
                ctrlKey:
                  event.originalEvent.ctrlKey || event.originalEvent.metaKey,
              })
            );
          }
        })
        .on("doubletap", (event) => {
          if (
            propsRef.current.status !== "EDIT" &&
            propsRef.current.subType === "player"
          ) {
            dispatch(
              editObject({ actionId: props.id, changes: { status: "EDIT" } })
            );
          }
        })
        .draggable({
          cursorChecker(action, interactable, element, interacting) {
            return interacting ? "grabbing" : "grab";
          },
          inertia: false,
          modifiers: [
            interact.modifiers.restrict({
              restriction: "#editor_wrapper",
            }),
          ],
          onstart: () => {
            dispatch(
              interactionStart({
                id: props.id,
                point: "s",
                subType: props.subType,
              })
            );
          },

          onend: (event) => {
            Array.from(document.getElementsByClassName("target_hover")).forEach(
              (el) => {
                el.classList.remove("target_hover");
              }
            );

            let metaKey = event?.metaKey || event?.ctrlKey;

            let nextPos = [
              Helper.roundNumber(
                propsRef.current.pos[0] + dragDeltaRef.current.x,
                metaKey ? 0 : 2
              ),
              Helper.roundNumber(
                propsRef.current.pos[1] + dragDeltaRef.current.y,
                metaKey ? 0 : 2
              ),
            ];

            dispatch(
              moveObject({
                data: {
                  pos: nextPos,
                },
                id: props.id,
              })
            );

            setEditMode(null);
            setDragDelta({ x: 0, y: 0 });
            dragDeltaRef.current = { x: 0, y: 0 };
          },

          onmove: (event) => {
            event.preventDefault();
            var deltaX = event.dx / propsRef.current.pixelPerFoot;
            var deltaY = event.dy / propsRef.current.pixelPerFoot;

            dragDeltaRef.current.x += deltaX;
            dragDeltaRef.current.y += deltaY;

            let metaKey = event?.metaKey || event?.ctrlKey;

            let nextPos = [
              Helper.roundNumber(
                propsRef.current.pos[0] + dragDeltaRef.current.x,
                metaKey ? 0 : 2
              ),
              Helper.roundNumber(
                propsRef.current.pos[1] + dragDeltaRef.current.y,
                metaKey ? 0 : 2
              ),
            ];

            setEditMode("DRAG");

            setDragDelta({
              x: nextPos[0] - propsRef.current.pos[0],
              y: nextPos[1] - propsRef.current.pos[1],
            });

            try {
              document.getElementById("selection_coordinates_x").value =
                nextPos[0];
              document.getElementById("selection_coordinates_y").value =
                nextPos[1];
            } catch (e) {}

            if (propsRef.current.subType === "handoff") {
              var target = event.target;
              let newX =
                (parseFloat(dragDeltaRef.current.x) || 0) +
                parseFloat(target.getAttribute("cx"));
              let newY =
                (parseFloat(dragDeltaRef.current.y) || 0) +
                parseFloat(target.getAttribute("cy"));

              propsRef.current.highlightHandoffPlayers(
                [newX, newY],
                playerCircleRadiusRef.current
              );
            }
          },
        });

      return () => interact("#" + props.uId).unset();
    }
  }, []); //eslint-disable-line

  let pixelPerFoot = props.pixelPerFoot;

  let factor = props.objectSizeFactor ?? 1;

  let {
    coneWidth,
    arrowStrokeWidth,
    playerBoxWidth,
    playerCircleRadius,
    handOffWidth,
  } = props.objectSizes;

  playerCircleRadius *= factor;
  coneWidth *= factor;
  handOffWidth *= factor;
  playerBoxWidth *= factor;
  arrowStrokeWidth *= factor;

  const playerCircleRadiusRef = useCurrentValue(playerCircleRadius);

  let linkedHandoffPaths = props.getLinkedHandoffPaths(
    props.pos,
    playerCircleRadius
  );

  let showHandoffPaths =
    (props.status === "SELECTED" ||
      props.status === "INTERACTING" ||
      props.status === "HOVERING") &&
    props.editable;

  const theme = useTheme();

  const [editMode, setEditMode] = useState(false);
  const [rotateData, setRotateData] = useState(null);

  const rotateDataRef = useCurrentValue(rotateData);
  const selectionRectangeRef = useRef(null);

  const handeRotateStart = (event) => {
    const rectElement = selectionRectangeRef.current;
    const rect = rectElement.getBoundingClientRect();
    const centerPageCoords = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };

    setRotateData({
      centerPageCoords,
      startAngle: Helper.getAngle(centerPageCoords, {
        x: event.clientX,
        y: event.clientY,
      }),
      clientCoords: {
        x: event.clientX,
        y: event.clientY,
      },
    });
    setEditMode("ROTATE");
  };

  const handleRotateMove = (event) => {
    setRotateData((oldData) => ({
      ...oldData,
      clientCoords: {
        x: event.clientX,
        y: event.clientY,
      },
    }));
  };

  const handleRotateEnd = (event) => {
    setEditMode(null);
    setRotateData(null);

    let tempAngle = Helper.getAngle(
      rotateDataRef.current.centerPageCoords,
      rotateDataRef.current.clientCoords
    );

    let nextAngle =
      tempAngle -
      rotateDataRef.current.startAngle +
      (propsRef.current.angle ?? 0);

    dispatch(
      editObject({
        actionId: props.id,
        changes: {
          angle: Math.round(nextAngle),
        },
      })
    );
  };

  const translate = { x: 0, y: 0 };
  let angle =
    props.team === "defense_alt"
      ? (props.angle ?? 0) + (props.court.full ? -90 : 0)
      : 0;
  let [centerX, centerY] = [props.pos[0], props.pos[1]];

  if (editMode === "DRAG") {
    translate.x = dragDelta.x;
    translate.y = dragDelta.y;
  }

  if (editMode === "ROTATE") {
    let tempAngle = Helper.getAngle(
      rotateData.centerPageCoords,
      rotateData.clientCoords
    );

    angle =
      tempAngle -
      rotateData.startAngle +
      (props.angle ?? 0) +
      (props.court.full ? -90 : 0);
  }

  const effectiveStatus = Helper.getEffectiveStatus(props);

  let isSelected = [
    "SELECTED",
    "HOVERING",
    "INTERACTING",
    "SELECTED_MULTIPLE",
    "HOVERING_ANIMATOR",
  ].includes(effectiveStatus);

  return (
    <>
      {props.subType === "handoff" && showHandoffPaths && (
        <>
          <path
            key={props.uId + "_handoff_path_0" + props.status}
            id={props.uId + "_handoff_path_0"}
            d={linkedHandoffPaths[0]}
            fill="none"
            stroke="rgba(240, 241, 243,0.6  )"
            strokeWidth={arrowStrokeWidth}
          />
          <path
            key={props.uId + "_handoff_path_1" + props.status}
            id={props.uId + "_handoff_path_1"}
            d={linkedHandoffPaths[1]}
            fill="none"
            stroke="rgba(240, 241, 243,0.6)"
            strokeWidth={arrowStrokeWidth}
          />
        </>
      )}

      <g
        id={props.uId}
        transform={`translate(${translate.x} ${translate.y}) `}
        //data-rotate={`rotate(${angle}deg)`}
        data-transform-origin={`${centerX}px ${centerY}px`}
        className={
          "draggable " +
          (props.linkedObjectHovering ? "linked_hovering" : "") +
          (props.team === "defense_alt" ? " defense_alt" : "")
        }
        onClick={(e) => {
          if (props.editable) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
        style={{
          WebkitTapHighlightColor: "transparent",
          outline: "none",
        }}
        cx={props.pos[0]}
        cy={props.pos[1]}
        data-team={props.team}
      >
        <g
          style={{
            transform: `rotate(${angle}deg)`,
            transformOrigin: `${centerX}px ${centerY}px`,
          }}
          //transform={`rotate(${angle} ${centerX} ${centerY})`}
        >
          {isSelected && (
            <>
              <rect
                ref={selectionRectangeRef}
                style={{ fill: "rgb(240, 241, 243)", fillOpacity: "0.5" }}
                className={
                  "draggable " +
                  (props.status === "SELECTED_MULTIPLE"
                    ? "selected_multiple"
                    : "selected")
                }
                x={props.pos[0] - playerBoxWidth / 2}
                y={props.pos[1] - playerBoxWidth / 2}
                height={playerBoxWidth}
                width={playerBoxWidth}
                stroke="black"
                strokeWidth={1 / pixelPerFoot}
              />
            </>
          )}
          {props.editable && (
            <circle
              id={props.uId + "_hover_circle"}
              css={{
                ".linked_hovering &": {
                  fill: "rgb(240, 241, 243)",
                  fillOpacity: 0.6,
                },
                fill: "none",
                ".target_hover &": {
                  fill: "rgb(240, 241, 243)",
                  fillOpacity: 1,
                },
              }}
              cx={props.pos[0]}
              cy={props.pos[1]}
              r={playerCircleRadius}
              strokeWidth={1 / pixelPerFoot}
            />
          )}
          {/*      // DONT DELETE RECT BELOW IMPORTANT FOR REGISTERING CLICKS
           */}
          <rect
            className="draggable"
            x={props.pos[0] - playerCircleRadius}
            y={props.pos[1] - playerCircleRadius}
            width={playerCircleRadius * 2}
            height={playerCircleRadius * 2}
            fill="transparent"
          ></rect>

          {props.animatable && (
            <circle
              id={props.uId + "_ball"}
              cx={props.pos[0]}
              cy={props.pos[1]}
              r={(playerCircleRadius * 47) / 50}
              fill={"transparent"}
              stroke={props.color}
              opacity={props.team === "ball" ? 1 : 0}
              strokeWidth={playerCircleRadius * 2 * 0.07}
              shapeRendering="geometricPrecision"
            />
          )}

          {props.team === "defense_alt" && effectiveStatus === "SELECTED" && (
            <>
              <line
                x1={props.pos[0]}
                y1={props.pos[1] - playerBoxWidth / 2}
                x2={props.pos[0]}
                y2={props.pos[1] - (props.court.full ? 4.5 : 3)}
                stroke={theme.palette.active}
                strokeWidth={2 / props.pixelPerFoot}
              />
              <DragCircle
                color="white"
                onStart={(e) => {
                  handeRotateStart(e);
                }}
                onMove={(dragDelta, e) => {
                  handleRotateMove(e);
                }}
                onEnd={(dragDelta, e) => {
                  handleRotateEnd(e);
                }}
                pixelPerFoot={props.pixelPerFoot}
                x={props.pos[0]}
                y={props.pos[1] - (props.court.full ? 4.5 : 3)}
              />
            </>
          )}

          {props.subType === "handoff" && (
            <>
              <mask id={"mask_" + props.uId}>
                <rect
                  fill="white"
                  opacity="2"
                  stroke="white"
                  x={0}
                  y={0}
                  width={150}
                  height={100}
                />
              </mask>

              <svg
                id={props.uId + "_handoff"}
                x={props.pos[0] - handOffWidth / 2}
                y={props.pos[1] - handOffWidth / 2}
                width={handOffWidth}
                height={handOffWidth}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 150 100"
              >
                <g mask={"url(#mask_" + props.uId + ")"}>
                  <path
                    strokeWidth="12"
                    stroke={props.color}
                    fill="none"
                    d="M 0 50 L 150 50"
                  />
                  <path
                    strokeWidth="12"
                    stroke={props.color}
                    fill="none"
                    d="M 50 0 L 50 150"
                  />
                  <path
                    strokeWidth="12"
                    stroke={props.color}
                    fill="none"
                    d="M 100 0 L 100 150"
                  />
                </g>
              </svg>
            </>
          )}

          {props.subType === "cone" && (
            <svg
              id={props.uId + "_cone"}
              x={props.pos[0] - coneWidth / 2}
              y={props.pos[1] - coneWidth / 2}
              width={coneWidth}
              height={coneWidth}
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill={props.color}
                d="M90.34,76.81a3.78,3.78,0,0,1-2.42,3.53L55.57,92.76a15.53,15.53,0,0,1-11.15,0L12.08,80.34a3.79,3.79,0,0,1-1.65-5.82,3.74,3.74,0,0,1,2-1.35l9.22-2.56a7.09,7.09,0,0,0,3.69,7A51.84,51.84,0,0,0,50,84.19,52,52,0,0,0,74.67,77.6a7.08,7.08,0,0,0,3.68-7l9.22,2.55A3.77,3.77,0,0,1,90.34,76.81ZM74.27,70.5,55.34,8c-.72-2.36-9.93-2.36-10.67,0L25.74,70.5a3.06,3.06,0,0,0,1.49,3.57q22.77,12.24,45.55,0A3.06,3.06,0,0,0,74.27,70.5Z"
              />
            </svg>
          )}

          {/*true && (
          <foreignObject
            backgroundColor="white"
            fontSize={1}
            x={props.pos[0] - 2 * playerCircleRadius}
            y={props.pos[1] + playerCircleRadius * 1.5}
            width={playerCircleRadius * 4}
            height={playerCircleRadius * 2.5}
          >
            <Stack
              sx={{
                justifyContent: "center",
                width: 1,
                height: 1,
                flex: 1,
                padding:
                  8 / props.pixelPerFoot +
                  "px " +
                  16 / props.pixelPerFoot +
                  "px",
                fontSize: 14 / props.pixelPerFoot,
                backgroundColor: "#0d99ff",
                color: "white",
              }}
            >
              <Box>X: {tempPos.x}</Box>
              <Box>Y: {tempPos.y}</Box>
            </Stack>
          </foreignObject>
            )*/}
        </g>
        <PlayerSVG
          stance={props.stance}
          angle={angle}
          changingNumber={(x) =>
            dispatch(
              editObject({
                actionId: props.id,
                changes: { number: x },
              })
            )
          }
          endInput={(x) => dispatch(tap({ id: props.id }))}
          edit={props.status === "EDIT" && props.editable}
          animatable={props.animatable}
          team={props.team}
          x={props.pos[0]}
          y={props.pos[1]}
          number={props.number}
          width={playerCircleRadius * 2}
          color={props.color}
        />
      </g>
    </>
  );
}

Player.propTypes = {
  id: PropTypes.string,
  uId: PropTypes.string,
  pos: PropTypes.array,
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
  team: PropTypes.string,
  status: PropTypes.oneOf([
    "SELECTED",
    "SELECTED_MULTIPLE",
    "EDIT",
    "INTERACTING",
    "NOT_SELECTED",
  ]),
  subType: PropTypes.oneOf(["player", "handoff", "cone", "hoop"]),
  editable: PropTypes.bool,
  animatable: PropTypes.bool,
};
